<template>
  <div>
    <search-sale-agent-monthly-performance
      statistic-type="stored"
      @submit="submitSearch"
    />

    <div class="simiot-table-operation-buttons">
      <a-button type="primary" @click="exportData">导出</a-button>
    </div>

    <a-table
      :scroll="{ x: 'max-content' }"
      size="middle"
      :columns="columns"
      :data-source="data"
      :loading="loading"
      :pagination="false"
      :row-class-name="rowClassName"
      row-key="id"
      @change="sortChange"
    >
      <div slot="action" slot-scope="text, record">
        <a @click="recalculate(record)">重新计算</a>
      </div>
    </a-table>

    <pagination
      v-show="pagination.total_count > 0"
      :total-count="pagination.total_count"
      :page.sync="query.page"
      :per-page.sync="query.per_page"
      @change="fetchData"
    />
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import SearchSaleAgentMonthlyPerformance from '@/views/sale_monthly_performances/info/Search'
import { exportSaleAgentMonthlyStoredPerformances, findSaleAgentMonthlyStorePerformances, recalculateSaleMonthlyPerformance } from '@/api/sale_monthly_performance'
import { exportExcel } from '@/api/excel'
import { formatCurrency } from '@/utils/filter'
import { amountColumn, canViewSaleCostProfit, cardFloorDiff, packageFloorDiff, salePerformanceRecalculateContent } from '@/utils/sale_monthly_performance'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'SaleMonthlyPerformanceInfoStoredList',
  components: {
    Pagination,
    SearchSaleAgentMonthlyPerformance
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      data: [],
      loading: true,
      pagination: {
        total_count: 0
      },
      query: {},
      sort: {
        sort_field: '',
        sort_order: ''
      }
    }
  },
  computed: {
    canRecalculate() {
      return hasPermission('sale_monthly_performances/recalculate')
    },
    canViewCostProfit() {
      return canViewSaleCostProfit(this.$store.getters.userRole)
    },
    columns() {
      const cols = [
        {
          title: '客户名称',
          dataIndex: 'agent_name'
        },
        {
          title: '账单类型',
          dataIndex: 'bill_type'
        },
        {
          title: '项目批次',
          dataIndex: 'agent_bill_no'
        }
      ]
      if (this.canRecalculate) {
        cols.push({
          title: '操作',
          width: 100,
          scopedSlots: { customRender: 'action' }
        })
      }
      cols.push(
        amountColumn('账单金额(元)', 'amount', formatCurrency, this.sort)
      )
      if (this.canViewCostProfit) {
        cols.push(amountColumn('毛利核算(元)', 'profit', formatCurrency, this.sort))
      }
      cols.push(
        {
          title: '数量',
          dataIndex: 'cards_count'
        },
        amountColumn('套餐费用(元)', 'product_amount', formatCurrency, this.sort),
        amountColumn('返佣费用(元)', 'rebate_fee', formatCurrency, this.sort),
        amountColumn('套餐底价(元)', 'floor_amount', formatCurrency, this.sort),
        {
          title: '套餐底价差(元)',
          customRender: (text, record) => formatCurrency(packageFloorDiff(record.product_amount, record.floor_amount, record.rebate_fee)),
          dataIndex: 'sale_profit',
          sorter: true,
          sortOrder: this.sort.sort_field === 'sale_profit' ? this.sort.sort_order : false
        },
        amountColumn('卡板费用(元)', 'card_amount', formatCurrency, this.sort),
        amountColumn('卡板底价(元)', 'card_floor_amount', formatCurrency, this.sort),
        {
          title: '卡板底价差(元)',
          customRender: (text, record) => formatCurrency(cardFloorDiff(record.card_amount, record.card_floor_amount)),
          dataIndex: 'card_floor_diff',
          sorter: true,
          sortOrder: this.sort.sort_field === 'card_floor_diff' ? this.sort.sort_order : false
        },
        amountColumn('快递费(元)', 'shipping_fee', formatCurrency, this.sort),
        {
          title: '账单时间',
          dataIndex: 'agent_bill_time'
        }
      )
      return cols
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    submitSearch(search) {
      this.sort = this.$options.data().sort
      this.query = Object.assign({},
        this.$options.data().query,
        { per_page: this.query.per_page },
        search)
      this.fetchData()
    },

    sortChange(pagination, filters, sorter) {
      this.sort.sort_field = sorter.field || ''
      this.sort.sort_order = sorter.order || ''
      this.query.page = 1
      this.fetchData()
    },

    fetchData() {
      this.loading = true
      findSaleAgentMonthlyStorePerformances(this.id, Object.assign({}, this.query, this.sort)).then((res) => {
        if (res.code === 0) {
          this.data = res.data
          this.pagination = res.pagination
        }
        this.loading = false
      })
    },

    rowClassName(record) {
      if (this.canViewCostProfit && record.profit != null && Number(record.profit) < 0) {
        return 'sale-profit-negative-row'
      }
      if (cardFloorDiff(record.card_amount, record.card_floor_amount) < 0) {
        return 'sale-profit-negative-row'
      }
      if (packageFloorDiff(record.product_amount, record.floor_amount, record.rebate_fee) < 0) {
        return 'sale-profit-negative-row'
      }
      return ''
    },

    exportData() {
      exportSaleAgentMonthlyStoredPerformances(this.id, Object.assign({}, this.query, this.sort)).then((res) => {
        if (res.code === 0 && res.data.is_direct_download) {
          exportExcel(res.data.id, res.data.file_name)
        }
      })
    },

    recalculate(record) {
      this.$confirm({
        title: '重新计算该账单的销售业绩？',
        content: salePerformanceRecalculateContent({ useLatestFloorPrice: true }),
        onOk: () => recalculateSaleMonthlyPerformance({
          sale_agent_monthly_performance_id: record.id
        })
      })
    }
  }
}
</script>

<style>
.sale-profit-negative-row td {
  color: #f5222d !important;
}
</style>
